import { useMemo } from "react";
import { RegtankLivenessSession } from "./useRegtankLivenessSession";
import {
  LivenessWebVerifyStatusEnum,
  MAX_LIVENESS_ATTEMPTS,
} from "../constants/enums";

export interface RegtankLivenessValidation {
  canAttempt: boolean;
  shouldVerify: boolean;
  isDisabled: boolean;
}

/**
 * Custom hook to validate Regtank liveness verification state
 * Determines if user can retry, if status should be verified, or if verification is disabled
 *
 * @param livenessSession - The liveness session data from useRegtankLivenessSession
 * @param maxAttempts - Maximum number of verification attempts allowed (default: MAX_LIVENESS_ATTEMPTS)
 * @returns RegtankLivenessValidation object with validation states
 */
export const useRegtankLivenessValidation = (
  livenessSession: RegtankLivenessSession | null,
  maxAttempts: number = MAX_LIVENESS_ATTEMPTS
): RegtankLivenessValidation => {
  return useMemo(() => {
    if (!livenessSession?.latestLiveness) {
      return { canAttempt: false, shouldVerify: false, isDisabled: false };
    }

    const { latestLiveness, isExpired } = livenessSession;
    const { livenessStatus, noOfAttemptLiveness, reattemptApproval } =
      latestLiveness;

    const hasFailedStatus =
      livenessStatus === LivenessWebVerifyStatusEnum.LIVENESS_FAILED ||
      livenessStatus === LivenessWebVerifyStatusEnum.FAILED;

    // User can retry if:
    // - Session not expired
    // - Attempts within limit (up to MAX_LIVENESS_ATTEMPTS = 4)
    // - Status is LIVENESS_FAILED (not hard FAILED)
    // On 4th attempt, Regtank will show "Liveness attempts exceeded"
    const statusNotFinal =
      livenessStatus === LivenessWebVerifyStatusEnum.LIVENESS_FAILED ||
      livenessStatus === null;
    const attemptIsNotExceeded = noOfAttemptLiveness < maxAttempts;
    const canRetry = !isExpired && attemptIsNotExceeded && statusNotFinal;

    // Verification is disabled if:
    // - Failed status AND reattempt not approved (null or REJECTED)
    const isDisabled =
      hasFailedStatus &&
      !canRetry &&
      (reattemptApproval === null || reattemptApproval === "REJECTED");

    // Should verify status (fetch final result from database) if:
    // - Verification succeeded (get final approved status) OR
    // - Liveness failed AND attempts exhausted (4th attempt or more, get final rejected status)
    // NOTE: Only checks LIVENESS_FAILED (soft fail), not hard FAILED status
    // canRetry prevents attempts beyond maxAttempts, so this acts as a safeguard
    const shouldVerifyStatus =
      livenessStatus === LivenessWebVerifyStatusEnum.SUCCESS ||
      (livenessStatus === LivenessWebVerifyStatusEnum.LIVENESS_FAILED &&
        noOfAttemptLiveness >= maxAttempts);

    return {
      canAttempt: canRetry,
      shouldVerify: shouldVerifyStatus,
      isDisabled,
    };
  }, [livenessSession, maxAttempts]);
};
